import { getColorwayName } from 'utils/formatters';
import { cn } from 'utils/styles';
import CardContent from '../CardContent';
import CardWrapper from '../CardWrapper';
import { CardStyle, HyperlinkedImagesLocation, IconVariantClassNames, IconVariantProps } from '../types';

import Image from 'next/image';

import { liveUpdateAttrs } from 'libs/contentful/utils';
import HyperlinkedMedia from '@/components/shared/HyperlinkedMedia/HyperlinkedMedia';
import CTA, { isValidCta } from '@/components/shared/CTA/CTA';

const IconVariant = ({
  sys,
  index,
  total,
  cardStyle,
  color,
  showFiveStarEyebrow,
  showChimeLogo,
  eyebrow,
  headline,
  copy,
  iconImage,
  hyperlinkedImages,
  hyperlinkedImagesLocation,
  cta,
  className,
}: IconVariantProps) => {
  const internalClassNames: IconVariantClassNames = {
    icon: cn('object-contain object-left w-fit h-[72px] mx-6 sm:mx-12', className?.icon),
    eyebrow: cn(className?.eyebrow),
    cardWrapper: {
      wrapper: cn(
        'pb-6 sm:pb-12',
        { 'flex flex-col gap-6': cardStyle !== CardStyle.None },
        className?.cardWrapper?.wrapper,
      ),
      header: cn(className?.cardWrapper?.header),
      content: cn('flex flex-col gap-6 flex-1', className?.cardWrapper?.content),
    },
    cardContent: {
      wrapper: cn(className?.cardContent?.wrapper),
      header: {
        wrapper: cn(className?.cardContent?.header?.wrapper),
        eyebrow: cn(className?.cardContent?.header?.eyebrow),
        headline: cn(className?.cardContent?.header?.headline),
      },

      copy: cn(className?.cardContent?.copy),
    },

    hyperlinkedImages: cn(
      'empty:hidden flex flex-col gap-2 mx-6 sm:mx-12',
      { 'grow-2 justify-start items-center': hyperlinkedImagesLocation === HyperlinkedImagesLocation.Bottom },
      className?.hyperlinkedImages,
    ),

    cta: cn('mx-6 sm:mx-12', className?.cta),
  };

  const testFixture = 'card-icon';

  const renderHyperlinkedImages = (testId: string) =>
    hyperlinkedImages ? (
      <div
        className={internalClassNames.hyperlinkedImages}
        data-testid={testId}
        {...liveUpdateAttrs(sys?.id, 'hyperlinkedImages')}>
        {(hyperlinkedImages?.items ?? []).map(({ link, image }) => (
          <HyperlinkedMedia
            key={image.sys?.id ?? image.url ?? `hyperlinked-image-${index}`}
            link={link}
            image={image}
          />
        ))}
      </div>
    ) : null;

  return (
    <CardWrapper
      cardStyle={cardStyle}
      index={index}
      total={total}
      color={getColorwayName(color)}
      className={internalClassNames.cardWrapper}
      testId={testFixture}>
      {iconImage && (
        <Image
          src={iconImage.url}
          alt={iconImage.description ?? ''}
          height={72}
          width={72}
          loading="lazy"
          className={internalClassNames.icon}
          data-testid={`${testFixture}-icon`}
          {...liveUpdateAttrs(sys?.id, 'icon')}
        />
      )}

      {hyperlinkedImagesLocation === HyperlinkedImagesLocation.Top
        ? renderHyperlinkedImages('card-wrapper-top-hli')
        : null}

      <CardContent
        sys={sys}
        showFiveStarEyebrow={showFiveStarEyebrow}
        showChimeLogo={showChimeLogo}
        eyebrow={eyebrow}
        headline={headline}
        copy={copy}
        testId={testFixture}
        className={internalClassNames.cardContent}
      />

      {hyperlinkedImagesLocation === HyperlinkedImagesLocation.Bottom
        ? renderHyperlinkedImages('card-wrapper-bottom-hli')
        : null}

      {isValidCta(cta) && (
        <div className={internalClassNames.cta}>
          <CTA cta={cta} />
        </div>
      )}
    </CardWrapper>
  );
};

export default IconVariant;
